import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { gql, useQuery } from '@apollo/client'
import { useLocation } from 'react-router-dom'
import {
  getActiveOrdersForNotifications,
  getRecentOrdersForNotifications,
  getRecentRestaurantOrdersForNotifications
} from '../apollo/queries'
import {
  isOrderSoundMuted,
  playNewOrderSound,
  setOrderSoundMuted
} from '../utils/orderAlertSound'

const GET_ACTIVE_ORDERS = gql`
  ${getActiveOrdersForNotifications}
`
const GET_RECENT_ORDERS = gql`
  ${getRecentOrdersForNotifications}
`
const GET_RECENT_RESTAURANT_ORDERS = gql`
  ${getRecentRestaurantOrdersForNotifications}
`

// Orders are polled instead of subscribed to: the order subscription the API
// exposes is per-restaurant, so it cannot feed the marketplace-wide feed the
// super admin needs.
// Kept short because this poll is what triggers the new-order chime.
const ACTIVE_POLL_INTERVAL = 10000
const RECENT_POLL_INTERVAL = 45000
// Only milestones from this window make it into the feed.
const FEED_WINDOW = 48 * 60 * 60 * 1000
const MAX_NOTIFICATIONS = 80
const MAX_TRACKED_ORDERS = 300
// Only orders placed this recently ring. It stops a backlog from ringing when
// a tab wakes up after hours asleep.
const ALERT_WINDOW = 10 * 60 * 1000
// While any order is still pending the chime repeats on this cadence (the
// chime itself is ~2.5s), so staff can't miss an order waiting to be accepted.
const PENDING_RING_INTERVAL = 4000

const NotificationsContext = createContext({
  notifications: [],
  unreadCount: 0,
  enabled: false,
  scopeLabel: '',
  markAsRead: () => {},
  markAllAsRead: () => {},
  clearAll: () => {},
  soundMuted: false,
  toggleSound: () => {},
  lastNewOrderAt: 0
})

const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user-borku')) || null
  } catch (e) {
    return null
  }
}

// Super admins and staff watch the whole marketplace; a vendor only ever sees
// the store they are currently signed into.
const resolveScope = () => {
  const user = readUser()
  if (!user) return { type: 'NONE', key: 'none' }
  const userType = user.userType
  if (userType === 'ADMIN' || userType === 'STAFF') {
    return { type: 'SUPER_ADMIN', key: `super_admin:${user.userId || 'all'}` }
  }
  if (userType === 'VENDOR') {
    // The store picker writes `restaurantId`; `restaurant_id` is the legacy key.
    const restaurantId =
      localStorage.getItem('restaurantId') ||
      localStorage.getItem('restaurant_id')
    if (!restaurantId) return { type: 'NONE', key: 'vendor:none' }
    return {
      type: 'RESTAURANT',
      restaurantId,
      key: `restaurant:${restaurantId}`
    }
  }
  return { type: 'NONE', key: 'none' }
}

const storageKey = scopeKey => `borku-notifications:${scopeKey}`
const stateKey = scopeKey => `borku-notifications-state:${scopeKey}`

const loadJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch (e) {
    return fallback
  }
}

const saveJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (e) {
    // Storage full or unavailable - the feed still works for this session.
  }
}

const humanStatus = status =>
  typeof status === 'string'
    ? status
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, c => c.toUpperCase())
    : ''

const orderLabel = order => `#${order.orderId || order._id}`

const toTime = value => {
  if (value === null || value === undefined || value === '') return NaN
  const numeric = Number(value)
  return Number.isNaN(numeric) ? new Date(value).getTime() : numeric
}

// Every milestone an order records (placed, accepted, rider assigned, picked
// up, delivered, cancelled) becomes one feed entry stamped with the time it
// happened. Ids are stable, so re-polling the same order never duplicates.
const eventsFor = (order, scopeType) => {
  const restaurantName = order.restaurant ? order.restaurant.name : ''
  // A vendor already knows which store they are in, so the store name is only
  // worth repeating in the marketplace-wide feed.
  const showStore = scopeType === 'SUPER_ADMIN' && !!restaurantName
  const store = showStore ? restaurantName : 'The store'
  const suffix = showStore ? ` · ${restaurantName}` : ''
  const riderName = (order.rider && order.rider.name) || 'A rider'
  const label = orderLabel(order)
  const base = {
    orderId: order._id,
    orderLabel: label,
    restaurantName
  }
  const cancelledWhilePending = !order.acceptedAt
  const milestones = [
    {
      key: 'new',
      at: order.createdAt,
      type: 'NEW_ORDER',
      title: `New order ${label}`,
      body: showStore
        ? `A new order was placed at ${restaurantName}`
        : 'A new order was placed'
    },
    {
      key: 'status:ACCEPTED',
      at: order.acceptedAt,
      type: 'ACCEPTED',
      title: `Order ${label} accepted`,
      body: `${store} accepted the order`
    },
    {
      key: 'rider',
      at: order.assignedAt,
      type: 'RIDER',
      title: `Rider assigned to ${label}`,
      body: `${riderName} accepted the delivery${suffix}`
    },
    {
      key: 'pickedup',
      at: order.pickedAt,
      type: 'PICKUP',
      title: `Order ${label} picked up`,
      body: `${riderName} collected the order${suffix}`
    },
    {
      key: 'status:DELIVERED',
      at: order.deliveredAt,
      type: 'DELIVERED',
      title: `Order ${label} delivered`,
      body: `${
        order.rider ? `${riderName} delivered the order` : 'Order delivered'
      }${suffix}`
    },
    {
      key: 'status:CANCELLED',
      at:
        order.orderStatus === 'CANCELLED'
          ? order.cancelledAt || order.updatedAt
          : null,
      type: 'REJECTED',
      title: `Order ${label} ${
        cancelledWhilePending ? 'rejected' : 'cancelled'
      }`,
      body: cancelledWhilePending
        ? `${store} rejected the order`
        : `The order was cancelled${suffix}`
    }
  ]
  return milestones
    .map(event => ({ ...event, time: toTime(event.at) }))
    .filter(event => !Number.isNaN(event.time))
    .map(({ key, at, time, ...event }) => ({
      ...base,
      ...event,
      id: `${order._id}:${key}`,
      createdAt: new Date(time).toISOString(),
      time
    }))
}

// Courier status has no timestamp of its own, so it is still detected by
// comparing against the value seen on the previous poll.
const courierEvent = (order, previous, scopeType) => {
  const next = order.courierStatus || null
  if (!next || !previous || previous.c === next) return null
  const restaurantName = order.restaurant ? order.restaurant.name : ''
  const suffix =
    scopeType === 'SUPER_ADMIN' && restaurantName ? ` · ${restaurantName}` : ''
  return {
    orderId: order._id,
    orderLabel: orderLabel(order),
    restaurantName,
    id: `${order._id}:courier:${next}`,
    type: 'COURIER',
    title: `Courier update for ${orderLabel(order)}`,
    body: `Courier status is now ${humanStatus(next)}${suffix}`,
    createdAt: new Date().toISOString(),
    time: Date.now()
  }
}

export const NotificationsProvider = ({ children }) => {
  const location = useLocation()
  // Scope is re-derived on navigation: that is when a vendor picks a store and
  // when a session is logged in or out.
  const [scope, setScope] = useState(resolveScope)

  useEffect(() => {
    const nextScope = resolveScope()
    setScope(current => (current.key === nextScope.key ? current : nextScope))
  }, [location.pathname])

  const scopeKey = scope.key
  const enabled = scope.type !== 'NONE'

  const [notifications, setNotifications] = useState(() =>
    loadJSON(storageKey(scopeKey), [])
  )
  // Last seen signature per order id, persisted so a page refresh does not
  // replay every event again.
  const trackedRef = useRef(
    loadJSON(stateKey(scopeKey), { seeded: false, orders: {} })
  )

  useEffect(() => {
    trackedRef.current = loadJSON(stateKey(scopeKey), {
      seeded: false,
      orders: {}
    })
    setNotifications(loadJSON(storageKey(scopeKey), []))
  }, [scopeKey])

  const persist = useCallback(
    next => {
      saveJSON(storageKey(scopeKey), next)
      return next
    },
    [scopeKey]
  )

  const ingest = useCallback(
    orders => {
      if (!enabled || !Array.isArray(orders) || orders.length === 0) return
      const tracked = trackedRef.current
      // The first poll of a session back-fills recent history as already
      // read, so the feed is populated without a flood of unread badges.
      const seeding = !tracked.seeded
      const cutoff = Date.now() - FEED_WINDOW
      const created = []

      orders.forEach(order => {
        if (!order || !order._id) return
        const previous = tracked.orders[order._id]
        eventsFor(order, scope.type)
          .filter(event => event.time >= cutoff)
          .forEach(event => created.push({ ...event, read: seeding }))
        const courier = courierEvent(order, previous, scope.type)
        if (courier) created.push({ ...courier, read: seeding })
        tracked.orders[order._id] = {
          c: order.courierStatus || null,
          t: Date.now()
        }
      })

      // Keep the tracking map bounded to the most recently touched orders.
      const entries = Object.entries(tracked.orders)
      if (entries.length > MAX_TRACKED_ORDERS) {
        tracked.orders = entries
          .sort((a, b) => (b[1].t || 0) - (a[1].t || 0))
          .slice(0, MAX_TRACKED_ORDERS)
          .reduce((acc, [id, value]) => {
            acc[id] = value
            return acc
          }, {})
      }
      tracked.seeded = true
      saveJSON(stateKey(scopeKey), tracked)

      if (created.length === 0) return
      setNotifications(current => {
        const known = new Set(current.map(item => item.id))
        const fresh = created.filter(item => !known.has(item.id))
        if (fresh.length === 0) return current
        return persist(
          [...fresh, ...current]
            .sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            )
            .slice(0, MAX_NOTIFICATIONS)
        )
      })
    },
    [enabled, persist, scope.type, scopeKey]
  )

  const isRestaurantScope = scope.type === 'RESTAURANT'

  const { data: activeData } = useQuery(GET_ACTIVE_ORDERS, {
    variables: isRestaurantScope ? { restaurantId: scope.restaurantId } : {},
    skip: !enabled,
    pollInterval: ACTIVE_POLL_INTERVAL,
    fetchPolicy: 'network-only',
    onError: () => {}
  })

  const { data: recentAdminData } = useQuery(GET_RECENT_ORDERS, {
    variables: { page: 1, rows: 50 },
    skip: scope.type !== 'SUPER_ADMIN',
    pollInterval: RECENT_POLL_INTERVAL,
    fetchPolicy: 'network-only',
    onError: () => {}
  })

  const { data: recentRestaurantData } = useQuery(
    GET_RECENT_RESTAURANT_ORDERS,
    {
      variables: { restaurant: scope.restaurantId, page: 0, rows: 50 },
      skip: !isRestaurantScope,
      pollInterval: RECENT_POLL_INTERVAL,
      fetchPolicy: 'network-only',
      onError: () => {}
    }
  )

  useEffect(() => {
    if (activeData && activeData.getActiveOrders) {
      ingest(activeData.getActiveOrders)
    }
  }, [activeData, ingest])

  // The active-order feed drops orders once they are delivered or cancelled,
  // so the recent list is what surfaces those closing statuses.
  useEffect(() => {
    const paginated = recentAdminData && recentAdminData.allOrdersPaginated
    if (paginated && paginated.orders) ingest(paginated.orders)
  }, [recentAdminData, ingest])

  useEffect(() => {
    if (recentRestaurantData && recentRestaurantData.ordersByRestId) {
      ingest(recentRestaurantData.ordersByRestId)
    }
  }, [recentRestaurantData, ingest])

  const markAsRead = useCallback(
    id =>
      setNotifications(current =>
        persist(
          current.map(item => (item.id === id ? { ...item, read: true } : item))
        )
      ),
    [persist]
  )

  const markAllAsRead = useCallback(
    () =>
      setNotifications(current =>
        persist(current.map(item => ({ ...item, read: true })))
      ),
    [persist]
  )

  const clearAll = useCallback(() => setNotifications(persist([])), [persist])

  // Flag each batch of new orders for the bell animation. Anything already in
  // the feed when the page loads (or when the scope changes) is marked as
  // alerted, so a refresh or a store switch never replays old orders.
  const [soundMuted, setSoundMuted] = useState(isOrderSoundMuted)
  const [lastNewOrderAt, setLastNewOrderAt] = useState(0)
  const alertedRef = useRef(null)

  useEffect(() => {
    alertedRef.current = null
  }, [scopeKey])

  useEffect(() => {
    const ids = notifications.map(item => item.id)
    if (alertedRef.current === null) {
      alertedRef.current = new Set(ids)
      return
    }
    const alerted = alertedRef.current
    const cutoff = Date.now() - ALERT_WINDOW
    const fresh = notifications.filter(
      item =>
        item.type === 'NEW_ORDER' &&
        !item.read &&
        !alerted.has(item.id) &&
        item.time >= cutoff
    )
    ids.forEach(id => alerted.add(id))
    if (fresh.length === 0) return
    setLastNewOrderAt(Date.now())
  }, [notifications])

  // Keep ringing for as long as any order is pending. The active-order poll
  // is the source of truth, so the loop stops on the first poll after the
  // last pending order is accepted or rejected.
  const pendingCount =
    activeData && Array.isArray(activeData.getActiveOrders)
      ? activeData.getActiveOrders.filter(
          order => order && order.orderStatus === 'PENDING'
        ).length
      : 0

  useEffect(() => {
    if (!enabled || soundMuted || pendingCount === 0) return
    playNewOrderSound()
    const timer = setInterval(playNewOrderSound, PENDING_RING_INTERVAL)
    return () => clearInterval(timer)
  }, [enabled, soundMuted, pendingCount])

  const toggleSound = useCallback(() => {
    setSoundMuted(current => {
      const next = !current
      setOrderSoundMuted(next)
      return next
    })
  }, [])

  // Show the unread count in the tab title (same number as the bell badge),
  // so staff on another tab can see it from the tab bar.
  const unreadCount = notifications.filter(item => !item.read).length
  const baseTitleRef = useRef(document.title.replace(/^\(\d+\+?\) /, ''))
  useEffect(() => {
    document.title =
      unreadCount > 0
        ? `(${unreadCount > 99 ? '99+' : unreadCount}) ${baseTitleRef.current}`
        : baseTitleRef.current
  }, [unreadCount])

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      enabled,
      scopeType: scope.type,
      markAsRead,
      markAllAsRead,
      clearAll,
      soundMuted,
      toggleSound,
      lastNewOrderAt
    }),
    [
      notifications,
      enabled,
      scope.type,
      markAsRead,
      markAllAsRead,
      clearAll,
      soundMuted,
      toggleSound,
      lastNewOrderAt
    ]
  )

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  )
}

export const useNotifications = () => useContext(NotificationsContext)

export default NotificationsContext
